import { initialState, LoginInfoState } from '@/config/initialState';
import { store } from '@/config/store';
import { Car, CustomerInfoApiResponse } from '@/types/api/customerApi';
import { getCustomerInfoRestrictionRequest } from '@/utils/prepaidRestrictionRequest';
import {
  findRelevantNextWarranty,
  getPreferredCurrentWarranty,
} from '@/utils/warranty';

type StoreStateUpdate<StateName extends string> = StateName extends 'loginInfo'
  ? Partial<LoginInfoState>
  : unknown;

export const storeDispatch = async <StateName extends string>(
  actions: string,
  stateName: StateName,
  newState: StoreStateUpdate<StateName>,
) => {
  await store.dispatch(actions, {
    [stateName]: newState,
  });
};

export const dispatchBasicInfo = async (
  customerInfo: CustomerInfoApiResponse['data'],
  requestedAffiliateId: number | null,
  findFnc?: (value: Car) => unknown,
) => {
  const context = getCustomerInfoRestrictionRequest(customerInfo.customer);
  if (
    (customerInfo.customer?.id &&
      customerInfo.customer.id !== store.state.authInfo.m_customer_id) ||
    (context && context.customerId !== store.state.authInfo.m_customer_id)
  )
    return;
  const {
    customer,
    cars,
    bill_infomations,
    payment_methods,
    prepaids,
    informations,
    campaigns,
  } = customerInfo;
  // API 発行時に確定した対象法人を記録する。現在の選択先から再計算すると、
  // 応答待ち中の切り替えで表示中データと記録する法人が食い違う。
  const customerInfoAffiliateId =
    requestedAffiliateId ?? customer?.m_affiliate_id ?? null;
  const promises = [
    storeDispatch('setCustomerInfo', 'customerInfo', customer),
    storeDispatch(
      'setCustomerInfoAffiliateId',
      'customerInfoAffiliateId',
      customerInfoAffiliateId,
    ),
    storeDispatch('setCarsInfo', 'carsInfo', cars),
    storeDispatch('setBillsInfo', 'billsInfo', bill_infomations),
    storeDispatch(
      'setPaymentMethodsInfo',
      'paymentMethodsInfo',
      payment_methods,
    ),
    storeDispatch('setAdvancePaymentsInfo', 'advancePaymentsInfo', prepaids),
    storeDispatch('setNotificationsInfo', 'notificationsInfo', informations),
    storeDispatch('setCampaignsInfo', 'campaignsInfo', campaigns),
  ];
  const car =
    (findFnc ? cars?.find(findFnc) : cars?.[0]) ||
    cars?.[0] ||
    initialState.carItemInfo;
  const warranties = car.warranties || [];
  const nextWarranties = car.next_warranty || [];
  const preferredWarranty = getPreferredCurrentWarranty(warranties);
  promises.push(
    storeDispatch('setCarItemInfo', 'carItemInfo', car),
    storeDispatch('setWarranties', 'warranties', warranties),
    storeDispatch('setNextWarranties', 'nextWarranties', nextWarranties),
    storeDispatch('setCurrentWarranty', 'currentWarranty', preferredWarranty),
    storeDispatch(
      'setNextWarranty',
      'nextWarranty',
      findRelevantNextWarranty(preferredWarranty, nextWarranties),
    ),
  );
  await Promise.all(promises);
};
